import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";

// ─── Data ────────────────────────────────────────────────────────────────────

const PAKISTAN_PROVINCES = [
  { label: "Punjab", cities: ["Lahore", "Faisalabad", "Rawalpindi", "Gujranwala", "Multan", "Sialkot", "Bahawalpur", "Sargodha", "Sheikhupura", "Jhang"] },
  { label: "Sindh", cities: ["Karachi", "Hyderabad", "Sukkur", "Larkana", "Nawabshah", "Mirpur Khas", "Thatta", "Khairpur"] },
  { label: "Khyber Pakhtunkhwa", cities: ["Peshawar", "Abbottabad", "Mardan", "Mingora", "Kohat", "Bannu", "Dera Ismail Khan"] },
  { label: "Balochistan", cities: ["Quetta", "Gwadar", "Turbat", "Khuzdar", "Hub", "Chaman", "Zhob"] },
  { label: "Azad Kashmir", cities: ["Muzaffarabad", "Mirpur", "Rawalakot", "Bagh", "Kotli"] },
  { label: "Gilgit-Baltistan", cities: ["Gilgit", "Skardu", "Hunza", "Ghanche", "Ghizer"] },
  { label: "Islamabad Capital Territory", cities: ["Islamabad"] },
];

const BUSINESS_TYPES = [
  { value: "manufacturer", label: "Manufacturer", icon: "🏭" },
  { value: "retailer", label: "Retailer", icon: "🏪" },
  { value: "exporter", label: "Exporter / Importer", icon: "🚢" },
  { value: "distributor", label: "Distributor", icon: "📦" },
  { value: "ecommerce", label: "E-Commerce", icon: "🛒" },
  { value: "other", label: "Other", icon: "💼" },
];

const INDUSTRIES = [
  "Food & Beverage", "Pharmaceuticals", "FMCG", "Agriculture",
  "Electronics", "Textile & Apparel", "Chemicals", "Automotive",
  "E-Commerce / Logistics", "Cosmetics & Personal Care", "Other",
];

const TOTAL_STEPS = 4;

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepBar({ current }) {
  const labels = ["Location", "Business", "Details", "Contact"];
  return (
    <div className="flex items-center justify-center gap-0 mb-12">
      {labels.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-400
                ${done ? "bg-brand-green text-white" : active ? "bg-brand-green text-white ring-4 ring-brand-green/20" : "bg-gray-100 text-gray-400"}`}
              >
                {done ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : step}
              </div>
              <span className={`mt-1.5 text-[10px] uppercase tracking-widest font-semibold ${active ? "text-brand-green" : "text-gray-400"}`}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className={`w-16 md:w-24 h-px mx-1 mb-5 transition-all duration-500 ${done ? "bg-brand-green" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Slide animation ──────────────────────────────────────────────────────────

const slide = (dir = 1) => ({
  initial: { opacity: 0, x: dir * 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: dir * -40 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
});

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Contact() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    province: "",
    city: "",
    businessType: "",
    businessName: "",
    industry: "",
    name: "",
    email: "",
    boxSpecs: "",
    message: "",
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const next = () => { setDir(1); setStep((s) => s + 1); };
  const back = () => { setDir(-1); setStep((s) => s - 1); };

  const selectedProvince = PAKISTAN_PROVINCES.find((p) => p.label === form.province);

  // ── Validation per step ──
  const canNext = () => {
    if (step === 1) return form.province && form.city;
    if (step === 2) return form.businessType && form.businessName && form.industry;
    if (step === 3) return form.name && form.email && form.boxSpecs;
    return true;
  };

  // ── Submit via EmailJS ──
  const handleSubmit = async () => {
    setSending(true);
    setError("");
    try {
      await emailjs.send(
        "service_jnls3qu",       // 🔁 replace with your EmailJS service ID
        "template_y9krrop",      // 🔁 replace with your EmailJS template ID
        {
          to_email: "info@pmspackages.com",
          province: form.province,
          city: form.city,
          business_type: BUSINESS_TYPES.find((b) => b.value === form.businessType)?.label,
          business_name: form.businessName,
          industry: form.industry,
          contact_name: form.name,
          contact_email: form.email,
          box_specs: form.boxSpecs,
          message: form.message || "N/A",
        },
        "Ymb_BkDTFk58rzWQI"        // 🔁 replace with your EmailJS public key
      );
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Top bar */}
      <div className="bg-dark text-white px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-gray-400 hover:text-white transition text-sm">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>
        <span className="text-xs uppercase tracking-widest text-gray-500 font-semibold">PMS Packages — Quote Request</span>
        <div className="w-24" />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-2xl">

          {!sent ? (
            <>
              <div className="text-center mb-8">
                <h1 className="font-heading text-3xl md:text-4xl text-gray-900 mb-2">Request a Quote</h1>
                <p className="text-gray-500 text-sm">Complete the steps below and we'll get back to you within 1–2 business days.</p>
              </div>

              <StepBar current={step} />

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 min-h-[360px] flex flex-col">
                <AnimatePresence mode="wait">

                  {/* ── STEP 1: Location ── */}
                  {step === 1 && (
                    <motion.div key="step1" {...slide(dir)} className="flex-1 flex flex-col">
                      <h2 className="font-heading text-2xl mb-1">Where are you located?</h2>
                      <p className="text-gray-400 text-sm mb-6">Select your province and city across Pakistan.</p>

                      <div className="grid md:grid-cols-2 gap-4 mb-auto">
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Province / Territory</label>
                          <select
                            value={form.province}
                            onChange={(e) => { set("province", e.target.value); set("city", ""); }}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-green transition bg-white"
                          >
                            <option value="">Select province…</option>
                            {PAKISTAN_PROVINCES.map((p) => (
                              <option key={p.label} value={p.label}>{p.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">City</label>
                          <select
                            value={form.city}
                            onChange={(e) => set("city", e.target.value)}
                            disabled={!form.province}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-green transition bg-white disabled:opacity-50"
                          >
                            <option value="">Select city…</option>
                            {selectedProvince?.cities.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* ── STEP 2: Business ── */}
                  {step === 2 && (
                    <motion.div key="step2" {...slide(dir)} className="flex-1 flex flex-col">
                      <h2 className="font-heading text-2xl mb-1">Tell us about your business</h2>
                      <p className="text-gray-400 text-sm mb-6">This helps us recommend the right packaging solution.</p>

                      {/* Business type */}
                      <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Business Type</label>
                      <div className="grid grid-cols-3 gap-2 mb-5">
                        {BUSINESS_TYPES.map((bt) => (
                          <button
                            key={bt.value}
                            onClick={() => set("businessType", bt.value)}
                            className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border text-sm transition-all duration-200
                              ${form.businessType === bt.value
                                ? "border-brand-green bg-brand-green/5 text-brand-green font-semibold"
                                : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                          >
                            <span className="text-xl">{bt.icon}</span>
                            <span className="text-xs leading-tight text-center">{bt.label}</span>
                          </button>
                        ))}
                      </div>

                      {/* Business name */}
                      <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Business Name</label>
                      <input
                        value={form.businessName}
                        onChange={(e) => set("businessName", e.target.value)}
                        placeholder="e.g. Al-Fatah Enterprises"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-green transition mb-5"
                      />

                      {/* Industry */}
                      <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Industry</label>
                      <div className="flex flex-wrap gap-2">
                        {INDUSTRIES.map((ind) => (
                          <button
                            key={ind}
                            onClick={() => set("industry", ind)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all duration-200
                              ${form.industry === ind
                                ? "bg-brand-green text-white border-brand-green"
                                : "border-gray-200 text-gray-500 hover:border-brand-green hover:text-brand-green"}`}
                          >
                            {ind}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* ── STEP 3: Requirements ── */}
                  {step === 3 && (
                    <motion.div key="step3" {...slide(dir)} className="flex-1 flex flex-col">
                      <h2 className="font-heading text-2xl mb-1">Your contact & requirements</h2>
                      <p className="text-gray-400 text-sm mb-6">We'll use these details to prepare your quote.</p>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Full Name *</label>
                          <input
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            placeholder="Ahmed Khan"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-green transition"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Email Address *</label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            placeholder="ahmed@business.com"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-green transition"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Required Box Size / Specs *</label>
                        <input
                          value={form.boxSpecs}
                          onChange={(e) => set("boxSpecs", e.target.value)}
                          placeholder='e.g. 30×20×15 cm, double wall, 5,000 units'
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-green transition"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-semibold">Additional Message</label>
                        <textarea
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                          rows={3}
                          placeholder="Any special requirements, print needs, or questions…"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-green transition resize-none"
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* ── STEP 4: Review ── */}
                  {step === 4 && (
                    <motion.div key="step4" {...slide(dir)} className="flex-1 flex flex-col">
                      <h2 className="font-heading text-2xl mb-1">Review & Submit</h2>
                      <p className="text-gray-400 text-sm mb-6">Confirm your details before we send your quote request.</p>

                      <div className="space-y-3 mb-auto text-sm">
                        {[
                          ["Location", `${form.city}, ${form.province}`],
                          ["Business Type", BUSINESS_TYPES.find((b) => b.value === form.businessType)?.label],
                          ["Business Name", form.businessName],
                          ["Industry", form.industry],
                          ["Contact Name", form.name],
                          ["Email", form.email],
                          ["Box Specs", form.boxSpecs],
                          ["Message", form.message || "—"],
                        ].map(([label, val]) => (
                          <div key={label} className="flex gap-3 py-2.5 border-b border-gray-100 last:border-0">
                            <span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold w-32 flex-shrink-0 pt-0.5">{label}</span>
                            <span className="text-gray-700 leading-relaxed">{val}</span>
                          </div>
                        ))}
                      </div>

                      {error && (
                        <p className="mt-4 text-sm text-red-500 bg-red-50 px-4 py-3 rounded-xl">{error}</p>
                      )}
                    </motion.div>
                  )}

                </AnimatePresence>

                {/* Nav buttons */}
                <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
                  <button
                    onClick={back}
                    className={`flex items-center gap-2 text-sm text-gray-400 hover:text-gray-700 transition ${step === 1 ? "invisible" : ""}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                  </button>

                  <div className="text-xs text-gray-300 font-medium">Step {step} of {TOTAL_STEPS}</div>

                  {step < TOTAL_STEPS ? (
                    <button
                      onClick={next}
                      disabled={!canNext()}
                      className="flex items-center gap-2 px-6 py-2.5 bg-brand-green text-white text-sm font-semibold rounded-xl hover:bg-brand-blue transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Continue
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={sending}
                      className="flex items-center gap-2 px-6 py-2.5 bg-brand-green text-white text-sm font-semibold rounded-xl hover:bg-brand-blue transition-colors duration-200 disabled:opacity-60"
                    >
                      {sending ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Sending…
                        </>
                      ) : (
                        <>
                          Submit Request
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* ── SUCCESS ── */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-brand-green" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="font-heading text-3xl mb-3">Request Sent!</h2>
              <p className="text-gray-500 text-sm max-w-sm mx-auto leading-relaxed mb-2">
                Thanks, <strong>{form.name}</strong>. Your quote request has been sent to our team.
              </p>
              <p className="text-gray-400 text-xs mb-8">We'll get back to you at <strong>{form.email}</strong> within 1–2 business days.</p>
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 bg-brand-green text-white text-sm uppercase tracking-widest font-semibold rounded-xl hover:bg-brand-blue transition-colors duration-200"
              >
                Back to Home
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}